import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaArrowLeft, FaPrint, FaFilePdf, FaSpinner } from 'react-icons/fa';
import RequerimentoDetranForm from '../components/RequerimentoDetranForm';
import { useToast } from '../components/ToastContext';
import config from '../config/config';
import { salvarVeiculo } from '../others/cadastroVeiculo';

const RequerimentoDetranPage = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [cliente, setCliente] = useState(null);
  const [tipoRequerimento, setTipoRequerimento] = useState('');
  const [placa, setPlaca] = useState('');
  const [chassi, setChassi] = useState('');
  const [marcaModelo, setMarcaModelo] = useState('');
  const [adquirente, setAdquirente] = useState(null); // só na ATPV-e
  const [valorVenda, setValorVenda] = useState(null); // só na ATPV-e
  // Motivo que impede imprimir/salvar o PDF (ex.: ATPV-e sem valor); null = liberado
  const [bloqueioImpressao, setBloqueioImpressao] = useState(null);
  const [cidadeEmpresa, setCidadeEmpresa] = useState('');
  const [carregando, setCarregando] = useState(true);

  // ─── CORREÇÃO: estado de loading do PDF + ref para capturar o formulário ──
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const encoded = params.get('data');

      if (!encoded) {
        addToast('warning', 'Nenhum cliente selecionado');
        navigate('/home');
        return;
      }

      // Decodifica Base64 -> JSON
      const json = decodeURIComponent(escape(atob(encoded)));
      const payload = JSON.parse(json);

      if (!payload?.cliente) {
        addToast('warning', 'Nenhum cliente selecionado');
        navigate('/home');
        return;
      }

      setCliente(payload.cliente);
      setTipoRequerimento(payload.tipoRequerimento || 'SERVICO_DECLARACAO');
      setPlaca(payload.placa || '');
      setChassi(payload.chassi || '');
      setMarcaModelo(payload.marcaModelo || '');
      setAdquirente(payload.adquirente || null);
      setValorVenda(payload.valorVenda ?? null);
      setCidadeEmpresa(payload.cidadeEmpresa || '');
      setCarregando(false);
    } catch (e) {
      console.error('Erro ao decodificar dados do requerimento:', e);
      addToast('warning', 'Nenhum cliente selecionado');
      navigate('/home');
    }
  }, []);

  const handleVoltar = () => {
    window.close();
    navigate('/home');
  };

  // ─── CORREÇÃO: "Salvar" agora gera e baixa o PDF de fato ──────────────────
  // Usa html2canvas para capturar o formulário renderizado como imagem,
  // depois jsPDF para montar um PDF em A4 com essa imagem, preservando
  // o layout exato que o usuário vê na tela (igual ao botão Imprimir).
  //
  // ─── CORREÇÃO (redução de tamanho do arquivo): a captura em PNG gerava
  // arquivos de ~13MB porque PNG é sem perdas e esse formulário tem muitas
  // bordas/checkboxes com anti-aliasing em alta escala. Trocamos para JPEG
  // com qualidade alta (0.92) — visualmente idêntico para este tipo de
  // conteúdo — e ligamos a compressão nativa do jsPDF. Nenhuma funcionalidade
  // muda: mesmo layout, mesmo fluxo, mesmo botão. Só o peso final do arquivo.
  // Dados do veículo como estão na folha (o usuário pode ter corrigido placa, chassi ou marca/modelo aqui).
  // Ao imprimir ou salvar o PDF, atualiza o cadastro de veículos. Nunca bloqueia a geração do documento.
  const veiculoRef = useRef({});
  const handleVeiculoChange = useCallback((dados) => { veiculoRef.current = dados; }, []);
  const gravarVeiculo = () => salvarVeiculo(config.API_BASE_URL, veiculoRef.current);

  const handleSalvarPdf = async () => {
    if (bloqueioImpressao) {
      addToast('warning', bloqueioImpressao);
      return;
    }
    if (!formRef.current) {
      addToast('error', 'Não foi possível localizar o formulário para gerar o PDF');
      return;
    }

    setGerandoPdf(true);
    await gravarVeiculo();
    try {
      // Carrega html2canvas e jsPDF via CDN apenas quando necessário,
      // evitando aumentar o bundle inicial da aplicação.
      await Promise.all([
        loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js', 'html2canvas'),
        loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js', 'jspdf'),
      ]);

      const html2canvas = window.html2canvas;
      const { jsPDF } = window.jspdf;

      // ── CORREÇÃO DO BUG DE PROPORÇÃO ───────────────────────────────────
      // O elemento alvo é o próprio formulário (FormContainer, width: 210mm),
      // não o wrapper da página inteira. Usamos suas dimensões REAIS
      // (offsetWidth/offsetHeight) em vez de forçar windowWidth/windowHeight
      // com base no scroll da página — isso evitava que html2canvas incluísse
      // o fundo cinza e o padding da página ao redor, fazendo o conteúdo do
      // formulário aparecer encolhido e deslocado no topo do PDF gerado.
      const elementoFormulario = formRef.current.querySelector('.print-area') || formRef.current;

      const canvas = await html2canvas(elementoFormulario, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        width: elementoFormulario.offsetWidth,
        height: elementoFormulario.offsetHeight,
        windowWidth: elementoFormulario.offsetWidth,
        windowHeight: elementoFormulario.offsetHeight,
        scrollX: 0,
        scrollY: 0,
        // ── CORREÇÃO: limpa placeholders e normaliza inputs no DOM CLONADO ──
        // html2canvas ignora @media print, então os placeholders ("nº
        // identidade", "ABC-1234" etc.) apareciam visíveis no PDF mesmo
        // quando o campo estava vazio. O onclone roda sobre uma cópia do
        // DOM ANTES da captura — alteramos só essa cópia, sem afetar o
        // formulário real que o usuário está vendo na tela.
        onclone: (clonedDoc) => {
          // Pega os inputs originais (ainda na página real) na MESMA ORDEM
          // que os do clone, para conseguir ler o estilo computado real do
          // navegador. getComputedStyle em elementos de um doc clonado fora
          // do fluxo de renderização pode retornar valores inconsistentes
          // entre navegadores, então usamos o original como fonte de verdade.
          // Os dois lados precisam olhar só para dentro da folha (.print-area): campos fora dela (ex.: o
          // painel de busca do adquirente da ATPV-e) desalinhavam a ordem e cada valor caía no campo seguinte.
          const areaClonada = clonedDoc.querySelector('.print-area') || clonedDoc;
          const originaisInputs = Array.from(elementoFormulario.querySelectorAll('input, textarea'));
          const clonadosInputs = Array.from(areaClonada.querySelectorAll('input, textarea'));

          clonadosInputs.forEach((input, idx) => {
            const original = originaisInputs[idx];

            // Remove o placeholder do clone para nunca ser desenhado
            input.removeAttribute('placeholder');

            // Substitui cada <input> de texto por um <span> com o mesmo
            // texto e estilo visual. html2canvas renderiza <span> de forma
            // muito mais previsível que <input> (que depende de estilos
            // nativos do navegador para baseline, padding interno, etc.,
            // e é a causa do texto "flutuando"/desalinhado visto no PDF).
            if (input.tagName === 'INPUT' && input.type !== 'checkbox' && input.type !== 'radio' && original) {
              const span = clonedDoc.createElement('span');
              span.textContent = original.value || '';
              const computed = window.getComputedStyle(original);
              span.style.cssText = `
                display: inline-block;
                font-family: ${computed.fontFamily};
                font-size: ${computed.fontSize};
                font-weight: ${computed.fontWeight};
                color: ${computed.color};
                text-transform: ${computed.textTransform};
                border: none;
                border-bottom: 1px solid #000;
                padding: ${computed.padding};
                width: ${computed.width};
                min-width: ${computed.width};
                box-sizing: border-box;
                white-space: nowrap;
                overflow: hidden;
                vertical-align: baseline;
                line-height: ${computed.lineHeight};
              `;
              input.parentNode.replaceChild(span, input);
            }
          });
        },
      });

      // ── CORREÇÃO (tamanho do arquivo): JPEG com qualidade 0.92 em vez de
      // PNG. PNG é sem perdas e infla muito em conteúdo com muitas bordas
      // finas (checkboxes, linhas de assinatura, grades da tabela de
      // serviços). JPEG nessa qualidade é visualmente idêntico para este
      // tipo de documento e reduz drasticamente o tamanho do PDF final.
      const imgData = canvas.toDataURL('image/jpeg', 0.92);

      // Página A4 em mm — compress: true liga a compressão interna do jsPDF
      // (Flate), reduzindo ainda mais o peso do arquivo final sem alterar
      // nada visualmente.
      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Proporção real da imagem capturada (largura × altura em pixels do canvas)
      const proporcao = canvas.height / canvas.width;

      // Ajusta a imagem para ocupar a largura inteira da página A4,
      // calculando a altura proporcionalmente — assim a imagem nunca
      // fica distorcida nem sobra espaço vazio artificial.
      const imgWidth = pageWidth;
      const imgHeight = imgWidth * proporcao;

      let heightLeft = imgHeight;
      let position = 0;

      // Primeira página
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Páginas adicionais, caso o conteúdo seja mais alto que uma página A4
      while (heightLeft > 0) {
        position = -(imgHeight - heightLeft);
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const nomeArquivo = `${getTituloRequerimento().replace(/\s+/g, '_')}_${(cliente?.nome || cliente?.razaoSocial || 'cliente').replace(/\s+/g, '_')}.pdf`;
      pdf.save(nomeArquivo);

      addToast('success', 'PDF gerado e baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      addToast('error', 'Erro ao gerar PDF. Tente novamente.');
    } finally {
      setGerandoPdf(false);
    }
  };

  const handleImprimir = async () => {
    if (bloqueioImpressao) {
      addToast('warning', bloqueioImpressao);
      return;
    }
    await gravarVeiculo();
    window.print();
  };

  const getTituloRequerimento = () => {
    const titulos = {
      SERVICO_DECLARACAO:    'Requerimento de Serviço com Declaração',
      PRIMEIRO_EMPLACAMENTO: 'Requerimento de Primeiro Emplacamento',
      SEGUNDA_VIA_CRV:       'Requerimento de 2ª Via de CRV/ATPV-e',
      BAIXA_DEFINITIVA:      'Requerimento de Baixa Definitiva',
      PREENCHIMENTO_ATPVE:   'Requerimento de Preenchimento da ATPV-e',
      DECLARACAO_RESIDENCIA: 'Declaração de Residência',
    };
    return titulos[tipoRequerimento] || 'Requerimento DETRAN/RS';
  };

  if (carregando) {
    return (
      <PageContainer>
        <LoadingContainer>Carregando requerimento...</LoadingContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header className="no-print">
        <HeaderContent>
          <BackButton onClick={handleVoltar}>
            <FaArrowLeft /> Voltar
          </BackButton>
          <Title>
            {getTituloRequerimento()}
            {cliente && <Subtitle>Cliente: {cliente.nome || cliente.razaoSocial}</Subtitle>}
          </Title>
          <Actions>
            <ActionButton onClick={handleSalvarPdf} disabled={gerandoPdf || !!bloqueioImpressao} title={bloqueioImpressao || undefined}>
              {gerandoPdf
                ? <><FaSpinner className="spin" /> Gerando PDF...</>
                : <><FaFilePdf /> Salvar PDF</>
              }
            </ActionButton>
            <PrintButton onClick={handleImprimir} disabled={!!bloqueioImpressao} title={bloqueioImpressao || undefined}
              style={bloqueioImpressao ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}>
              <FaPrint /> Imprimir
            </PrintButton>
          </Actions>
        </HeaderContent>
      </Header>

      <Content>
        {/* Bloqueado (ex.: ATPV-e sem valor): mesmo pelo Ctrl+P do navegador a folha não sai impressa */}
        {bloqueioImpressao && (
          <>
            <style>{`@media print { .print-area { display: none !important; } .aviso-bloqueio-impressao { display: block !important; } }`}</style>
            <div className="aviso-bloqueio-impressao" style={{ display: 'none', fontSize: '14pt', padding: '20mm' }}>{bloqueioImpressao}</div>
            <div className="no-print" style={{ maxWidth: '210mm', margin: '0 auto 12px', padding: '10px 14px', borderRadius: 8, background: '#fdecea', border: '1px solid #f5b7b1', color: '#a93226', fontWeight: 600 }}>
              {bloqueioImpressao}
            </div>
          </>
        )}
        {cliente && (
          // ─── CORREÇÃO: ref no wrapper do formulário para o html2canvas capturar ──
          <div ref={formRef}>
            <RequerimentoDetranForm
              cliente={cliente}
              tipoRequerimento={tipoRequerimento}
              placaInicial={placa}
              chassiInicial={chassi}
              marcaInicial={marcaModelo}
              adquirenteInicial={adquirente}
              valorInicial={valorVenda}
              onBloqueioImpressaoChange={setBloqueioImpressao}
              onVeiculoChange={handleVeiculoChange}
              cidadeEmpresa={cidadeEmpresa}
            />
          </div>
        )}
      </Content>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </PageContainer>
  );
};

// ─── Helper: carrega um script externo apenas uma vez, via CDN ──────────────
function loadScript(src, globalCheckName) {
  return new Promise((resolve, reject) => {
    // Se já foi carregado (ex: window.html2canvas já existe), não duplica
    if (globalCheckName === 'html2canvas' && window.html2canvas) return resolve();
    if (globalCheckName === 'jspdf' && window.jspdf) return resolve();

    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Falha ao carregar script: ${src}`));
    document.body.appendChild(script);
  });
}

const PageContainer = styled.div`
  min-height: 100vh;
  background: #e8ecf0;
  display: flex;
  flex-direction: column;

  @media print {
    background: white;
    min-height: unset;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  font-size: 18px;
  color: #004d4d;
  font-family: Arial, sans-serif;
`;

const Header = styled.header`
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;

  @media print {
    display: none !important;
  }
`;

const HeaderContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: background 0.2s;

  &:hover {
    background: #5a6268;
  }
`;

const Title = styled.div`
  text-align: center;
  font-weight: 700;
  font-size: 16px;
`;

const Subtitle = styled.div`
  font-size: 12px;
  color: #6b7e90;
  margin-top: 4px;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background: linear-gradient(135deg, #004d4d 0%, #008080 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: transform 0.2s;
  white-space: nowrap;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const PrintButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background: linear-gradient(135deg, #27ae60 0%, #2ecc71 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: transform 0.2s;
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
  }
`;

const Content = styled.main`
  flex: 1;
  padding: 24px;

  @media print {
    padding: 0;
  }
`;

export default RequerimentoDetranPage;
